<template>
  <q-page class="flex flex-center" style="min-height: 100vh">
    <q-card class="register-card q-pa-lg" style="width: 100%; max-width: 420px">
      <q-card-section class="text-center q-mb-md">
        <h4 class="text-gold q-my-none">Olympic Academy</h4>
        <p class="text-grey-5 q-mt-sm">Crea tu cuenta</p>
      </q-card-section>

      <q-card-section>
        <q-banner
          v-if="error"
          class="bg-negative text-white q-mb-md"
          rounded
          dense
        >
          {{ error }}
        </q-banner>

        <q-form @submit.prevent="onSubmit" class="q-gutter-md">
          <q-input
            v-model="firstName"
            label="Nombre"
            filled
            dark
            :rules="[(val: string) => !!val || 'El nombre es obligatorio']"
            lazy-rules
          >
            <template #prepend>
              <q-icon name="person" />
            </template>
          </q-input>

          <q-input
            v-model="lastName"
            label="Apellido"
            filled
            dark
            :rules="[(val: string) => !!val || 'El apellido es obligatorio']"
            lazy-rules
          >
            <template #prepend>
              <q-icon name="person" />
            </template>
          </q-input>

          <q-input
            v-model="email"
            type="email"
            label="Email"
            filled
            dark
            :rules="[
              (val: string) => !!val || 'El email es obligatorio',
              (val: string) => /.+@.+\..+/.test(val) || 'Email invalido',
            ]"
            lazy-rules
          >
            <template #prepend>
              <q-icon name="email" />
            </template>
          </q-input>

          <q-input
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            label="Contrasena"
            filled
            dark
            :rules="[
              (val: string) => !!val || 'La contrasena es obligatoria',
              (val: string) => val.length >= 8 || 'Minimo 8 caracteres',
            ]"
            lazy-rules
          >
            <template #prepend>
              <q-icon name="lock" />
            </template>
            <template #append>
              <q-icon
                :name="showPassword ? 'visibility_off' : 'visibility'"
                class="cursor-pointer"
                @click="showPassword = !showPassword"
              />
            </template>
          </q-input>

          <q-input
            v-model="confirmPassword"
            :type="showPassword ? 'text' : 'password'"
            label="Confirmar contrasena"
            filled
            dark
            :rules="[
              (val: string) => !!val || 'Confirma tu contrasena',
              (val: string) => val === password || 'Las contrasenas no coinciden',
            ]"
            lazy-rules
          >
            <template #prepend>
              <q-icon name="lock" />
            </template>
          </q-input>

          <q-btn
            type="submit"
            label="Crear cuenta"
            color="amber-8"
            text-color="dark"
            class="full-width q-mt-md"
            size="lg"
            :loading="loading"
            :disable="loading"
            no-caps
          />
        </q-form>
      </q-card-section>

      <q-card-section class="text-center">
        <p class="text-grey-5">
          Ya tenes cuenta?
          <router-link to="/login" class="text-gold" style="text-decoration: none">
            Inicia sesion
          </router-link>
        </p>
      </q-card-section>
    </q-card>
  </q-page>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from 'stores/useAuthStore';
import { AxiosError } from 'axios';

const router = useRouter();
const authStore = useAuthStore();

const firstName = ref('');
const lastName = ref('');
const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const showPassword = ref(false);
const loading = ref(false);
const error = ref('');

async function onSubmit() {
  if (password.value !== confirmPassword.value) {
    error.value = 'Las contrasenas no coinciden';
    return;
  }

  error.value = '';
  loading.value = true;

  try {
    await authStore.register({
      email: email.value,
      password: password.value,
      firstName: firstName.value,
      lastName: lastName.value,
    });
    await router.push('/');
  } catch (err) {
    if (err instanceof AxiosError && err.response?.data?.message) {
      error.value = err.response.data.message as string;
    } else {
      error.value = 'Error al crear la cuenta. Intenta de nuevo.';
    }
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped lang="scss">
.register-card {
  border: 1px solid #333333;
}
</style>
